// frontend/src/services/api.js
import axios from "axios";

/**
 * Works in BOTH setups:
 * - Dev CRA proxy: API_BASE = ""  (requests go to same origin, proxy forwards to backend)
 * - Prod / custom: API_BASE = "https://your-domain.com" or "http://localhost:5000"
 */
const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * Transition normalizer (HARDENED):
 * - Absolute URLs: untouched
 * - "/api/..." stays
 * - "/api/api/..." collapses to "/api/..." (fixes your bug)
 * - Any other "/users", "/anime", "/reviews", etc => prefixed to "/api/..."
 */
function normalizeUrl(path) {
  const raw = String(path || "");

  // absolute URL => don't touch
  if (/^https?:\/\//i.test(raw)) return raw;

  // ensure leading slash
  const p = raw.startsWith("/") ? raw : `/${raw}`;

  // fix the exact bad path: "/api/api/..."
  if (p.startsWith("/api/api/")) {
    const fixed = p.replace(/^\/api\/api\//, "/api/");
    return `${API_BASE}${fixed}`;
  }

  // already has /api
  if (p.startsWith("/api/")) return `${API_BASE}${p}`;

  // default: prefix /api
  return `${API_BASE}/api${p}`;
}

const http = axios.create({
  // baseURL handled by normalizeUrl (so we can normalize per-request)
  withCredentials: true, // cookie auth compatible
});

async function post(path, data, config = {}) {
  const res = await http.post(normalizeUrl(path), data, config);
  return res.data;
}

async function get(path, config = {}) {
  const res = await http.get(normalizeUrl(path), config);
  return res.data;
}

/* -----------------------------
   Auth / Users
------------------------------ */

// Register
export const registerUser = async (userData) => {
  return post("/users", userData);
};

// Update bio & profile picture
export const updateUserProfile = async (userData) => {
  return post("/users/update-bio", userData);
};

/* -----------------------------
   Reviews
------------------------------ */

export const submitReview = async (reviewData) => {
  return post("/reviews", reviewData);
};

export const fetchAnimeReviews = async (animeId) => {
  return get(`/reviews/anime/${animeId}`);
};

/* -----------------------------
   Anime
------------------------------ */

export const fetchAnimeList = async () => {
  const data = await get("/anime");
  // Handle both response formats (with/without results wrapper)
  return data?.results || data;
};

// Search anime with pagination
export const searchAnime = async (query, page = 1, limit = 20) => {
  return get("/anime", {
    params: { search: query, page, limit },
  }); // Returns { results, pagination }
};

// Fetch anime by internal ID or AniList ID
export const fetchAnimeDetails = async (id, source = "internal") => {
  const params = source === "anilist" ? { source: "anilist" } : {};
  return get(`/anime/${id}`, { params });
};

// Fetch currently airing anime
export const fetchAiringAnime = async () => {
  return get("/anime/airing");
};

// Fetch anime by genre (client-side filter; keeps behavior identical)
export const fetchAnimeByGenre = async (genre) => {
  const data = await get("/anime");
  const animeList = data?.results || data || [];
  return animeList.filter(
    (anime) => anime?.Genres && anime.Genres.includes(genre)
  );
};
